/* Folder sa listićima: prevlačenje (pregledač ne pita ništa) ili izbor sa diska (input webkitdirectory).
   Rezultat: { naziv, slike: [{ file, putanja }] (redom po putanji, prirodno: 2 pre 10), preskoceno } */

const SLIKA = /\.(jpe?g|png|bmp|webp|gif)$/i;

const pripremi = (naziv, fajlovi) => {
    const slike = fajlovi.filter((x) => SLIKA.test(x.file.name))
        .sort((a, b) => a.putanja.localeCompare(b.putanja, 'sr', { numeric: true, sensitivity: 'base' }));
    return { naziv, slike, preskoceno: fajlovi.length - slike.length };
};

/** izabrani fajlovi iz inputa (webkitdirectory) */
export function izInputa(fileList) {
    const sve = Array.from(fileList || []);
    const putanja = (f) => f.webkitRelativePath || f.name;
    return pripremi(sve.length ? putanja(sve[0]).split('/')[0] : 'folder', sve.map((f) => ({ file: f, putanja: putanja(f) })));
}

/** prevučen folder (ili fajlovi): rekurzivno kroz FileSystemEntry */
export async function izPrevlacenja(dataTransfer) {
    const unosi = Array.from(dataTransfer.items || []).map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null)).filter(Boolean);
    const fajlovi = [];
    const fajl = (e) => new Promise((res, rej) => e.file(res, rej));
    const procitajSve = async (dir) => {
        const r = dir.createReader();
        const svi = [];
        /* readEntries vraća deo po deo (do 100) — čita se dok ne vrati prazno */
        for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const deo = await new Promise((res, rej) => r.readEntries(res, rej));
            if (!deo.length) break;
            svi.push(...deo);
        }
        return svi;
    };
    const obidji = async (e) => {
        if (e.isFile) fajlovi.push({ file: await fajl(e), putanja: e.fullPath.replace(/^\//, '') });
        else if (e.isDirectory) for (const d of await procitajSve(e)) await obidji(d); // eslint-disable-line no-await-in-loop
    };
    for (const e of unosi) await obidji(e); // eslint-disable-line no-await-in-loop
    return pripremi(unosi.length === 1 && unosi[0].isDirectory ? unosi[0].name : 'prevučeni fajlovi', fajlovi);
}

/* ---------- vozač iz naziva ----------
   Folderi i fajlovi se najčešće imenuju po vozaču ("Petkovic Milan/Petkovic Milan001.jpg"). Poređenje je bez kvačica i
   bez obzira na velika/mala slova; vozač se prihvata kad se i SVE reči prezimena i SVE reči imena nalaze u istom delu
   putanje (fajl, pa folderi ka korenu) i kad je takav vozač u izabranim kompanijama tačno jedan. */
const normalno = (s) => String(s || '').replace(/[đĐ]/g, 'dj').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const reci = (s) => normalno(s).split(/[^a-z]+/).filter((w) => w.length >= 2);

/** @returns vozač iz spiska ili null (nema poklapanja ili ih je više — tada odlučuje AI) */
export function vozacIzNaziva(putanja, vozaci) {
    if (!putanja || !vozaci || !vozaci.length) return null;
    const delovi = String(putanja).split('/');
    for (let k = delovi.length - 1; k >= 0; k--) {
        const r = new Set(reci(delovi[k].replace(/\.[a-z0-9]+$/i, '')));
        if (!r.size) continue; // eslint-disable-line no-continue
        const nadjeni = vozaci.filter((d) => {
            const p = reci(d.lastName), i = reci(d.firstName);
            return p.length && i.length && p.every((w) => r.has(w)) && i.every((w) => r.has(w));
        });
        const ids = new Set(nadjeni.map((d) => d.id));
        if (ids.size === 1) return nadjeni[0];
        if (ids.size > 1) return null;
    }
    return null;
}

/* ---------- predlozi vozača po sličnosti naziva ----------
   Kad naziv nije tačno ime vozača (greška u kucanju, skraćeno ime, drugi redosled), predlažu se najsličniji vozači
   izabranih kompanija: za svaku reč prezimena i imena najbliža reč iz naziva (fajl i folderi), sličnost po Levenštajnu. */
const lev = (a, b) => {
    const m = a.length, n = b.length;
    if (!m || !n) return Math.max(m, n);
    let prev = Array.from({ length: n + 1 }, (_, j) => j);
    for (let i = 1; i <= m; i++) {
        const cur = [i];
        for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        prev = cur;
    }
    return prev[n];
};
const slicnost = (a, b) => 1 - lev(a, b) / Math.max(a.length, b.length);

/** @returns do `n` vozača [{ vozac, ocena 0..1 }] sličnih nazivu (ocena >= 0.6), najsličniji prvi */
export function predloziIzNaziva(putanja, vozaci, n = 3) {
    if (!putanja || !vozaci || !vozaci.length) return [];
    const r = [...new Set(String(putanja).split('/').flatMap((d) => reci(d.replace(/\.[a-z0-9]+$/i, '').replace(/\d+/g, ' '))))];
    if (!r.length) return [];
    const ocene = vozaci.map((d) => {
        const w = [...reci(d.lastName), ...reci(d.firstName)];
        if (!w.length) return { vozac: d, ocena: 0 };
        const s = w.reduce((a, x) => a + Math.max(...r.map((y) => slicnost(x, y))), 0) / w.length;
        return { vozac: d, ocena: s };
    });
    return ocene.filter((x) => x.ocena >= 0.6).sort((a, b) => b.ocena - a.ocena).slice(0, n);
}
